import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { KybStepItem } from './KybStepItem';
import { KybDescription } from './KybDescription';

// import { CompanyVerification } from './CompanyVerification';
// import { CompanyRoles } from './CompanyRoles';
import { CompanyMembers } from './CompanyMembers';
import { KybStepsConfirmation } from './KybStepsConfirmation';

import { ErrorBoundary } from '../ErrorBoundary';
import { PANADATA_ROOT, getLanguage, isBeneficiariesStepComplete, isCompanyFinancialStepComplete, isContactsStepComplete, sendEvent } from '../../utils';

import type { ClaimRequest, Step } from '../../types';

import progressIcon from '../../assets/icons/progress.svg?raw&inline';
import { PrivacyPolicies } from './PrivacyPolicies';
import FinancialProfileStep from './financial-profile-step';

/**
 * BasicFlow - KYB wizard flow
 * 
 * Architecture: Steps are derived from data state (in parent index.tsx)
 * - No localStorage needed
 * - Refresh-safe (data comes from Rails DB)
 * - Multi-tab safe (all tabs read from same DB)
 * - Always shows overview on page load with checkmarks on completed steps
 */
export const BasicFlow: React.FC<BusinessClaimProps> = ({
  organization,
  members,
  contacts,
  currentUser,
  claim,
  privacyPolicyAccepted,
  steps,
  setSteps,
  currentStep,
  setCurrentStep,
}) => {
  const { t } = useTranslation();
  const language = getLanguage();
  
  const [submitting, setSubmitting] = useState(false);

  const handleCurrentStep = (index: number) => {
    return (index === 0 && steps[index].status === 'pending') ||
      (steps[index - 1] && steps[index - 1].status === 'completed') && steps[index].status === 'pending';
  };

  /**
   * Check if user has made progress (any steps completed)
   */
  const hasProgress = () => {
    return steps.some(step => step.status === 'completed');
  };

  /**
   * Start/Continue the flow - set currentStep to first pending
   */
  const handleNextStepClick = async () => {
    if(!currentUser) {
      const currentLocation = window.location.pathname + window.location.search;
      localStorage.setItem('stored_location', currentLocation);
      return window.location.href = `/${language}/users/simple_signup`;
    }
    // send mixpanel event on first step
    if (currentStep === 0 || currentStep === null || currentStep === undefined) {
      sendEvent({ 
        event_name: 'Usuario inicia flujo de Claim Request', 
        properties: { 
          organization_id: organization.id, 
          organization_name: organization.nombre 
        } 
      });
    }

    if (currentUser.id === null) {
      setSubmitting(true);
      return window.location.href = `/${language}/users/sign_up?ref=business_claim`;
    }

    const firstPendingIndex = steps.findIndex((item: Step) => item.status === 'pending');
    if (firstPendingIndex === -1) {
      // All steps completed, redirect this also is done on the event btn of KybStepCompleted
      window.location.href = `${PANADATA_ROOT}/${language}/business_claim?kcvs=true`;
      return;
    }
    const lastStepIndex = steps.length - 1;
    // evitar que se pase de largo el idx y mostrar pantalla en blanco
    // por lo general es el ultimo paso q esta pending de por vida y se desecha
    if(firstPendingIndex >= lastStepIndex) {
      setCurrentStep(lastStepIndex);
      return
    }

    // TODO: this is path cause CompanyMember have a miniwizard of 2 step
    // de modo que hardcodeamos donde ir dependiendo de la data
    const contactsStepComplete = isContactsStepComplete(contacts, members);
    const shareholdersStepComplete = isBeneficiariesStepComplete(contacts);
    const companyFinancialStepComplete = isCompanyFinancialStepComplete(claim);

    if (contactsStepComplete && !shareholdersStepComplete && !companyFinancialStepComplete) {
      setCurrentStep(0);
      return;
    }
    if (contactsStepComplete && companyFinancialStepComplete && shareholdersStepComplete) {
      setCurrentStep(2);
      return;
    }
    if (contactsStepComplete && shareholdersStepComplete){
      // After members/beneficiaries, go to financial profile step (step 1)
      setCurrentStep(1);
      return;
    }    
    setCurrentStep(firstPendingIndex);
  };

  /**
   * Mark current step as complete and move to next
   * Updates local state only - data is saved to Rails by each step component
   */
  const handleStepCompleted = () => {
    const updatedSteps: Step[] = steps.map((step: Step, i: number) => {
      // companyMember tiene mini wizard de 2 steps hay q sacarlo
      if (i === currentStep) return { ...step, status: 'completed' };
      return step;
    });
    setSteps(updatedSteps);

    // Check if all steps are completed
    const hasMorePendingSteps = updatedSteps.find((item: Step) => item.status === 'pending') !== undefined;
    
    if (!hasMorePendingSteps) {
      // All steps completed, redirect this also is done on the event btn of KybStepCompleted
      // All steps completed, redirect to dashboard
      // query params kcvs means kyb contact verification send so rails will display the flash message
      window.location.href = `${PANADATA_ROOT}/${language}/business_claim?kcvs=true`;
      return;
    }

    // Move directly to next pending step (use updatedSteps, not steps)
    const nextStepIndex = updatedSteps.findIndex((item: Step) => item.status === 'pending');
    
    // Handle navigation after completing financial profile step (step 1)
    if (currentStep === 1) {
      if (privacyPolicyAccepted) {
        // Skip privacy policies, go directly to confirmation (step 3)
        setCurrentStep(3);
        return;
      } else {
        // Go to privacy policies (step 2)
        setCurrentStep(2);
        return;
      }
    }
    
    // Handle navigation after completing privacy policies (step 2)
    if (currentStep === 2) {
      setCurrentStep(3);
      return;
    }
    
    // For other steps, use the standard logic
    if(privacyPolicyAccepted && nextStepIndex === 1) {
      // step 1 companyMembers con 2 miniwizard completados harcode this logic hasta
      // tener time pa refactor companyMembers, quitar el miniwizard interna
      setCurrentStep(2);
      return
    }
    setCurrentStep(nextStepIndex);
  };

  const handleEntityName = () => {
    if (organization) {
      return organization.nombre.length > 30
        ? `${organization.nombre.slice(0, 30)}...`
        : organization.nombre;
    }
  };

  return (
    <ErrorBoundary>
      {currentStep === null ? (
        <div className="row d-flex mt-5 mx-auto justify-content-center" style={{ maxWidth: 660 }}>
          <div className="badge rounded-pill text-uppercase size-12 font-weight-600 w-auto bg-kyb-pill mb-5 text-dark">
            {handleEntityName()}
          </div>
          <KybDescription
            title={t(`kyb_flow.steps.title`)}
            description={t(`kyb_flow.steps.description`)}
          />
          <div className="mt-4 p-0">
            {steps.map((step: Step, index: number) => (
              <KybStepItem
                key={`step_${index}`}
                index={index}
                step={step}
                current={handleCurrentStep(index)}
                hasLine={index + 1 !== steps.length}
                hideNumber={false}
              />
            ))}
          </div>
          <>
            <button
              className="btn btn-black w-100 mt-3 d-flex align-items-center justify-content-center"
              onClick={handleNextStepClick}
              disabled={submitting}
            >
              {submitting ? (
                <span 
                  className="spin" 
                  dangerouslySetInnerHTML={{ __html: progressIcon }} 
                />
              ) : (
                // Show "Continue" if user has progress, otherwise "Start"
                hasProgress() 
                  ? t('kyb_flow.steps.continue_button', { defaultValue: 'Continuar' })
                  : t('kyb_flow.steps.step_1_button')
              )}
            </button>
          </>
        </div>
      ) : (
        <div>
          {currentStep === 0 && (
            /** aqui hay mini-wizard usa member para tener 2 data roles y beneficiarios */
            <CompanyMembers
              privacyPolicyAccepted={privacyPolicyAccepted}
              organization={organization}
              members={members}
              contacts={contacts || []}
              claimRequest={claim}
              onStepCompleted={handleStepCompleted}
            />
          )}
          {currentStep === 1 && (
            <FinancialProfileStep
              data={{
                company_address: claim?.data?.company_address,
                company_phone: claim?.data?.company_phone,
                income_sources: claim?.data?.income_sources as ("ahorros" | "comercializacion de bienes y servicios" | "dividentos" | "aporte de accionistas" | "otros")[] | undefined,
                income_sources_other: claim?.data?.income_sources_other,
                income_countries: claim?.data?.income_countries,
                economic_activity_countries: claim?.data?.economic_activity_countries,
                annual_income_range: claim?.data?.annual_income_range,
              }}
              claimRequestId={claim?.id}
              userId={currentUser?.id}
              organization={organization}
              privacyPolicyAccepted={privacyPolicyAccepted}
              onStepCompleted={handleStepCompleted}
            />
          )}
          {currentStep === 2 && (
            <PrivacyPolicies
              currentStep={currentStep}
              organization={organization}
              currentUser={currentUser}
              privacyPolicyAccepted={privacyPolicyAccepted}
              onNextClick={handleStepCompleted}
              isSubmitting={submitting}
              claimRequestId={claim?.id}
            />
          )}
          {currentStep === 3 && (
            <KybStepsConfirmation
              claimRequest={claim}
              organization={organization}
            />
          )}
        </div>
      )}
    </ErrorBoundary>
  );
};

interface BusinessClaimProps {
  organization: any;
  members: any;
  contacts: any;
  currentUser?: any;
  claim: ClaimRequest;
  privacyPolicyAccepted: boolean;
  steps: Step[];
  setSteps: (steps: Step[]) => void;
  currentStep: number | null;
  setCurrentStep: (step: number | null) => void;
}
